/**
 * Order Detail Page
 *
 * Displays a single order with its details and associated CDs.
 */

import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Building,
  Calendar,
  DollarSign,
  Edit,
  Eye,
  FileText,
  Grid3X3,
  Home,
  List,
  MapPin,
  Plus,
  Trash2,
  Upload,
  User,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { DocumentsList } from '../components';
import { useDeleteOrder, useOrder, useOrderCDs, useOrderDocuments } from '../hooks';
import type { OrderStatus, OrderTransaction } from '../types';

function getStatusBadgeVariant(
  status: OrderStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  const variants: Record<OrderStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    open: 'default',
    reconciling: 'secondary',
    approved: 'default',
    closed: 'outline',
    cancelled: 'destructive',
  };
  return variants[status] || 'outline';
}

function getStatusLabel(status: OrderStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatCurrency(value: string | number | undefined): string {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(Number(value));
}

function getTransactionStatusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    draft: 'secondary',
    reconciling: 'default',
    approved: 'default',
    finalized: 'outline',
  };
  return variants[status] || 'outline';
}

function CDCard({ cd }: { cd: OrderTransaction }) {
  const navigate = useNavigate();

  return (
    <Card
      className="cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
      onClick={() => navigate(`/dashboard/cd/${cd.id}/edit`)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium">{cd.source_type || 'Manual Entry'}</h4>
                <Badge variant={getTransactionStatusBadgeVariant(cd.status)} className="text-xs">
                  {cd.status}
                </Badge>
              </div>
              {cd.source_party_name && (
                <p className="text-sm text-muted-foreground">From: {cd.source_party_name}</p>
              )}
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            v{cd.version || 1}
          </Badge>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Closing Date:</span>{' '}
            <span className="font-medium">
              {cd.closing_date ? format(new Date(cd.closing_date), 'MMM d, yyyy') : '-'}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Sale Price:</span>{' '}
            <span className="font-medium">{formatCurrency(cd.sale_price)}</span>
          </div>
        </div>

        {cd.property_address && (
          <div className="mt-3 pt-3 border-t text-sm">
            <span className="text-muted-foreground">Property:</span>{' '}
            <span className="font-medium truncate block">{cd.property_address}</span>
          </div>
        )}

        <div className="mt-3 pt-3 border-t flex items-center justify-between">
          {(cd.last_version_date || cd.updatedAt) && (
            <span className="text-xs text-muted-foreground">
              Last edited:{' '}
              {format(new Date(cd.last_version_date || cd.updatedAt), 'MMM d, yyyy h:mm a')}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/cd/${cd.id}/preview`);
            }}
          >
            <Eye className="h-4 w-4 mr-1" />
            Preview
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CDListItem({ cd }: { cd: OrderTransaction }) {
  const navigate = useNavigate();

  return (
    <div
      className="p-4 flex items-center justify-between hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => navigate(`/dashboard/cd/${cd.id}/edit`)}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{cd.source_type || 'Manual Entry'}</span>
            <Badge variant={getTransactionStatusBadgeVariant(cd.status)} className="text-xs">
              {cd.status}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
            {cd.closing_date && (
              <span>Closing: {format(new Date(cd.closing_date), 'MMM d, yyyy')}</span>
            )}
            {cd.sale_price && <span>{formatCurrency(cd.sale_price)}</span>}
            {cd.property_address && !cd.closing_date && !cd.sale_price && (
              <span className="truncate max-w-[250px]">{cd.property_address}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        {(cd.last_version_date || cd.updatedAt) && (
          <span className="text-xs text-muted-foreground">
            {format(new Date(cd.last_version_date || cd.updatedAt), 'MMM d, yyyy')}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cd/${cd.id}/preview`);
          }}
          title="Preview CD"
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Badge variant="outline" className="text-xs">
          v{cd.version || 1}
        </Badge>
      </div>
    </div>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageHeader, clearPageHeader } = usePageHeader();
  const queryClient = useQueryClient();
  const [cdViewMode, setCdViewMode] = useState<'grid' | 'list'>('list');

  const { data: order, isLoading: orderLoading, error: orderError } = useOrder(id || '');
  const { data: cds, isLoading: cdsLoading } = useOrderCDs(id || '');
  const { data: documents, isLoading: documentsLoading } = useOrderDocuments(id || '');

  // Check for refresh state and refetch data
  useEffect(() => {
    if (location.state?.refresh) {
      // Refetch all queries for this order
      queryClient.invalidateQueries({ queryKey: ['orders', 'order', id] });
      queryClient.invalidateQueries({ queryKey: ['orders', id, 'cds'] });
      queryClient.invalidateQueries({ queryKey: ['orders', 'documents', 'order', id] });
    }
  }, [location.state, id, queryClient]);

  // Filter out deleted CDs
  const activeCDs = cds?.filter((cd) => cd.status !== 'deleted') || [];

  // Set page header
  useEffect(() => {
    setPageHeader({
      title: order?.order_number || 'Order Details',
      breadcrumbs: [
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Orders', href: '/orders' },
        { label: order?.order_number || 'Loading...' },
      ],
    });
    return () => clearPageHeader();
  }, [setPageHeader, clearPageHeader, order?.order_number]);

  const deleteOrder = useDeleteOrder({
    onSuccess: () => {
      navigate('/orders');
    },
  });

  const handleAddCD = () => {
    navigate(`/dashboard/create-cd?orderId=${id}`);
  };

  const handleUploadCD = () => {
    navigate(`/orders/${id}/upload-cd`);
  };

  const handleEdit = () => {
    navigate(`/orders/${id}/edit`);
  };

  const handleDelete = () => {
    if (id) {
      deleteOrder.mutate(id);
    }
  };

  if (orderLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (orderError || !order) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Order Not Found</h2>
        <p className="text-muted-foreground mt-2">The requested order could not be found.</p>
        <Button className="mt-4" onClick={() => navigate('/orders')}>
          Return to Orders
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant={getStatusBadgeVariant(order.status)}>
            {getStatusLabel(order.status)}
          </Badge>
          {order.order_name && <span className="text-muted-foreground">{order.order_name}</span>}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Order</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this order? This action cannot be undone. All
                  associated CDs will also be deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Tabbed Content */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="property">Property</TabsTrigger>
          <TabsTrigger value="parties">Parties</TabsTrigger>
          <TabsTrigger value="cds" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            CDs
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Order Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="flex items-center gap-3">
                  <Home className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Transaction Type</p>
                    <p className="font-medium capitalize">{order.transaction_type}</p>
                  </div>
                </div>

                {order.settlement_agency && (
                  <div className="flex items-center gap-3">
                    <Building className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Settlement Agency</p>
                      <p className="font-medium">{order.settlement_agency}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Sale Price</p>
                    <p className="font-medium">{formatCurrency(order.sale_price)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Target Closing Date</p>
                    <p className="font-medium">
                      {order.target_closing_date
                        ? format(new Date(order.target_closing_date), 'MMMM d, yyyy')
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {order.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap">{order.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Property Tab */}
        <TabsContent value="property" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Property Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{order.property_address}</p>
                  {order.property_unit && (
                    <p className="text-muted-foreground">Unit {order.property_unit}</p>
                  )}
                  <p className="text-muted-foreground">
                    {order.property_city}, {order.property_state} {order.property_zip}
                  </p>
                  {order.property_county && (
                    <p className="text-muted-foreground text-sm">{order.property_county} County</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Parties Tab */}
        <TabsContent value="parties" className="space-y-6">
          {order.parties && order.parties.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Transaction Parties</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {order.parties.map((party) => (
                    <div key={party.id} className="flex items-start gap-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          party.party_type === 'buyer'
                            ? 'bg-blue-100 dark:bg-blue-900/50'
                            : party.party_type === 'seller'
                              ? 'bg-purple-100 dark:bg-purple-900/50'
                              : party.party_type === 'lender'
                                ? 'bg-green-100 dark:bg-green-900/50'
                                : 'bg-orange-100 dark:bg-orange-900/50'
                        }`}
                      >
                        {party.party_type === 'lender' ? (
                          <Building
                            className={`h-5 w-5 ${
                              party.party_type === 'lender'
                                ? 'text-green-600 dark:text-green-400'
                                : ''
                            }`}
                          />
                        ) : party.party_type === 'listing_agent' ||
                          party.party_type === 'selling_agent' ? (
                          <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        ) : (
                          <User
                            className={`h-5 w-5 ${
                              party.party_type === 'buyer'
                                ? 'text-blue-600 dark:text-blue-400'
                                : party.party_type === 'seller'
                                  ? 'text-purple-600 dark:text-purple-400'
                                  : 'text-muted-foreground'
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-muted-foreground capitalize">
                          {party.party_type.replace('_', ' ')}
                        </p>
                        <p className="font-medium truncate">{party.name}</p>
                        {party.email && (
                          <p className="text-sm text-muted-foreground truncate">{party.email}</p>
                        )}
                        {party.phone && (
                          <p className="text-sm text-muted-foreground">{party.phone}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">No Parties Added</h3>
                  <p className="text-muted-foreground">
                    No parties have been added to this order yet.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CDs Tab */}
        <TabsContent value="cds" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Closing Disclosures</h2>
              {activeCDs.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({activeCDs.length} CD{activeCDs.length > 1 ? 's' : ''})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* View Toggle */}
              {activeCDs.length > 0 && (
                <div className="flex items-center border rounded-md">
                  <Button
                    variant={cdViewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="rounded-r-none"
                    onClick={() => setCdViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={cdViewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="rounded-l-none"
                    onClick={() => setCdViewMode('grid')}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={handleUploadCD}>
                <Upload className="h-4 w-4 mr-2" />
                Upload CD
              </Button>
              <Button size="sm" onClick={handleAddCD}>
                <Plus className="h-4 w-4 mr-2" />
                Add CD
              </Button>
            </div>
          </div>

          {cdsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : activeCDs.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">No CDs Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Add your first Closing Disclosure to this order.
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" onClick={handleUploadCD}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload CD
                    </Button>
                    <Button onClick={handleAddCD}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create CD
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : cdViewMode === 'list' ? (
            <div className="border rounded-lg divide-y">
              {activeCDs.map((cd) => (
                <CDListItem key={cd.id} cd={cd} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeCDs.map((cd) => (
                <CDCard key={cd.id} cd={cd} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Uploaded Documents</h2>
          </div>
          <DocumentsList orderId={id || ''} documents={documents} isLoading={documentsLoading} />
        </TabsContent>
      </Tabs>

      {/* Order Metadata */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              {order.creator && <span>Created by {order.creator.name || order.creator.email}</span>}
              {order.created_at && (
                <span>Created on {format(new Date(order.created_at), 'MMM d, yyyy')}</span>
              )}
            </div>
            {order.updated_at && (
              <span>Last updated {format(new Date(order.updated_at), 'MMM d, yyyy')}</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
